import type { Patient } from '../../services/patientsService';
import { getPatientActivityStatus } from '../../services/patientsService';
import type { SissiBand } from '../../services/sissiScoreService';

export const fullName = (patient: Patient) =>
  `${patient.firstName} ${patient.lastName}`.trim() || patient.username;

export interface SissiRing {
  score: number | null;
  band: SissiBand | null;
  isReliable: boolean;
}

/**
 * El anillo pinta la media de 7 días, no el día suelto: el diario cae a 0 en
 * cuanto el paciente no registra, y una columna que parpadea entre 0 y 80 no se
 * puede escanear. El día concreto sigue en el tooltip.
 *
 * Sin media = ningún día participado en la ventana, que es el perfil de
 * abandono. Si el último día puntuado es HOY, ese número sí describe el
 * presente (el silencio con plan puntúa 0) y se pinta: es la señal de "ha
 * dejado de registrar", que si no se perdería en un anillo vacío idéntico al de
 * un paciente recién creado. Si es de días atrás no se pinta nada: un 85 de
 * hace diez días no es el estado de hoy.
 *
 * La pista discontinua (isReliable false) avisa de que detrás del número hay
 * poca o ninguna semana.
 */
export function sissiRing(patient: Patient, today: string): SissiRing {
  const s = patient.sissiScore;
  const rolling = s?.rolling7 ?? null;
  const dailyIsToday = !rolling && !!s && s.score !== null && s.date === today;

  return {
    score: rolling ? rolling.score : dailyIsToday ? s!.score : null,
    band: rolling ? rolling.band : dailyIsToday ? s!.band : null,
    isReliable: rolling ? rolling.isReliable : !dailyIsToday,
  };
}

export interface ActivityFlag {
  label: string;
  /** Tailwind classes for the pill; the dot form reuses the same colour. */
  pill: string;
  dot: string;
}

/** Returns null for a patient who is active, which is the common case and
 *  deliberately shows no badge at all. */
export function activityFlag(patient: Patient): ActivityFlag | null {
  switch (getPatientActivityStatus(patient)) {
    case 'never':
      return {
        label: 'Nunca accedió',
        pill: 'bg-destructive/12 text-destructive',
        dot: 'bg-destructive',
      };
    case 'inactive':
      return {
        label: 'Sin actividad +7 días',
        pill: 'bg-warning/15 text-warning',
        dot: 'bg-warning',
      };
    default:
      return null;
  }
}

export const truncateEmail = (email: string | null | undefined, max = 30) =>
  !email ? '—' : email.length > max ? `${email.slice(0, max)}…` : email;

export const todayIso = () => new Date().toISOString().split('T')[0];
